import React, {
  useRef,
  useState,
  useContext,
  useCallback,
  useEffect,
} from 'react';

import { AppContext } from '@prisjakt/ui-app-context';
import Search from 'src/components/search/search';
import { Search as SearchInput } from 'src/components/inputs';

import RelatedProducts from './components/related-products';

import { Product, RelatedProduct } from '../../types';
import { OnSelect } from './types';

import * as S from './styles';

const INPUT_MIN_LENGTH = 2;

const SEARCH_OPTIONS = {
  groups: [
    {
      limit: 5,
      name: 'products',
      modes: ['product'],
    },
  ],
};

type Props = {
  showNewComparePageLayout?: boolean;
  relatedProductsAsOverlay?: boolean;
  showFloatingResult?: boolean;
  focusOnMount?: boolean;
  placeholder?: string;
  onSelect: OnSelect;
  relatedProducts?: RelatedProduct[];
};

const ProductSearch = ({
  showNewComparePageLayout = false,
  relatedProductsAsOverlay = false,
  showFloatingResult = true,
  focusOnMount,
  placeholder,
  onSelect,
  relatedProducts,
}: Props) => {
  const searchRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const { t } = useContext(AppContext);

  const [inputValue, setInputValueState] = useState('');
  const [search, setSearchState] = useState('');
  const [hasResultsState, setHasResultsState] = useState(false);
  const [results, setResultsState] = useState([]);

  const resetSearch = () => {
    setSearchState('');
    setHasResultsState(false);
  };

  const handleOnChange = useCallback((searchTerm: string) => {
    setSearchState(searchTerm);
    setHasResultsState(false);
  }, []);

  const handleOnFocus = useCallback((searchTerm: string) => {
    if (searchTerm.length >= INPUT_MIN_LENGTH) {
      setSearchState(searchTerm);
      setHasResultsState(true);
    }
  }, []);

  const setInputValue = useCallback((input: string) => {
    setInputValueState(input);
  }, []);

  const handleOnSelect = useCallback(
    (product: Product) => {
      setInputValueState('');
      resetSearch();

      return onSelect(product);
    },
    [onSelect]
  );

  const handleSearchResultFound = useCallback((...items: any[]) => {
    setHasResultsState(true);
    setResultsState(items[0]?.hits ?? []);
  }, []);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      resetSearch();
    }
  }, []);

  const handleClickOutside = useCallback((e: MouseEvent) => {
    if (
      e.target &&
      searchRef.current &&
      !searchRef.current.contains(e.target as Node)
    ) {
      resetSearch();
    }
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('click', handleClickOutside);

    if (focusOnMount && searchInputRef.current) {
      const inputElement: HTMLInputElement | null = searchInputRef.current.querySelector(
        'input[type=text]'
      );

      if (inputElement) {
        inputElement.focus();
      }
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('click', handleClickOutside);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const searchIsOpen = inputValue.length >= INPUT_MIN_LENGTH && search !== '';
  const hasResults =
    hasResultsState && search !== '' && results && results.length > 0;

  return (
    <S.SearchWrapper ref={searchRef}>
      <S.Search showNewComparePageLayout={showNewComparePageLayout}>
        <S.SearchInput ref={searchInputRef} searchIsOpen={searchIsOpen}>
          <SearchInput
            key="search"
            placeholder={
              placeholder || t('search', 'search-products.placeholder')
            }
            hasResults={hasResults}
            onChange={handleOnChange}
            onFocus={handleOnFocus}
            inputValue={inputValue}
            setInputValue={setInputValue}
          />
        </S.SearchInput>
        <S.SearchResult
          searchIsOpen={searchIsOpen}
          showFloatingResult={showFloatingResult}
          showNewComparePageLayout={showNewComparePageLayout}
        >
          <Search
            query={search}
            onSelect={handleOnSelect}
            showAllResults={false}
            keyboardNavigation={false}
            styling="none"
            disableScrollToTop
            onSearchResultFound={handleSearchResultFound}
            options={SEARCH_OPTIONS}
            inputMinLength={INPUT_MIN_LENGTH}
          />
        </S.SearchResult>
      </S.Search>

      {relatedProducts &&
        Boolean(relatedProducts.length) &&
        !hasResults &&
        Boolean(!searchIsOpen) && (
          <RelatedProducts
            relatedProducts={relatedProducts}
            relatedProductsAsOverlay={relatedProductsAsOverlay}
            onSelect={onSelect}
            showNewComparePageLayout={showNewComparePageLayout}
          />
        )}
    </S.SearchWrapper>
  );
};

export default ProductSearch;
